/**
 * 🚀 [Phase v13.47 — 2026-07-18] Composant Lightbox pour photos de messagerie.
 *
 * Fonctionnalités :
 *   - Ouverture au clic sur une photo dans la conversation (via prop `fichier`)
 *   - Zoom CSS pur via `transform: scale()` — range 0.5 → 5
 *       • Molette (wheel) : zoom in/out centré souris
 *       • Boutons +/− (data-testid `msg-photo-zoom-in|out`)
 *       • Double-clic image : reset scale=1
 *   - Pan (drag & drop) quand `scale > 1` : souris + touch mobile
 *   - Bouton « Télécharger » : lien `<a download>` avec `data:URL`
 *   - Fermeture : X (natif Shadcn Dialog), Esc, click-outside
 *
 * Aucune dépendance externe — CSS pur + Shadcn `<Dialog>`.
 * FR-CA strict pour tous les labels/tooltips visibles.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, Download, RotateCcw, X } from 'lucide-react';

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;

export function MessagePhotoLightbox({ fichier, open, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const imgRef = useRef(null);

  // Reset zoom/pan à chaque ouverture
  useEffect(() => {
    if (open) {
      setScale(1);
      setOffset({ x: 0, y: 0 });
    }
  }, [open, fichier?.data_b64]);

  const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

  const zoomIn = useCallback(() => setScale(s => clampScale(s + ZOOM_STEP)), []);
  const zoomOut = useCallback(() => {
    setScale(s => {
      const next = clampScale(s - ZOOM_STEP);
      if (next <= 1) setOffset({ x: 0, y: 0 });   // recentre si retour à 1
      return next;
    });
  }, []);
  const resetZoom = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };
  const handleMouseMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  };
  const handleMouseUp = () => {
    dragRef.current.dragging = false;
  };

  // Touch handlers (mobile pan)
  const handleTouchStart = (e) => {
    if (scale <= 1 || !e.touches?.[0]) return;
    dragRef.current = {
      dragging: true,
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      baseX: offset.x,
      baseY: offset.y,
    };
  };
  const handleTouchMove = (e) => {
    if (!dragRef.current.dragging || !e.touches?.[0]) return;
    const dx = e.touches[0].clientX - dragRef.current.startX;
    const dy = e.touches[0].clientY - dragRef.current.startY;
    setOffset({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  };
  const handleTouchEnd = () => {
    dragRef.current.dragging = false;
  };

  if (!fichier) return null;
  const dataUrl = `data:${fichier.type};base64,${fichier.data_b64}`;
  const isZoomed = scale > 1.01;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-6xl w-[95vw] max-h-[95vh] p-0 bg-slate-900 border-slate-700"
        data-testid="msg-photo-lightbox"
        onWheel={handleWheel}
      >
        {/* 🚀 [Phase v13.47 — 2026-07-18] A11y : DialogTitle/Description
              visuellement cachés pour lecteurs d'écran (Radix requires this). */}
        <DialogTitle className="sr-only">Aperçu de la photo</DialogTitle>
        <DialogDescription className="sr-only">
          Utilisez la molette pour zoomer, le double-clic pour réinitialiser,
          Esc pour fermer.
        </DialogDescription>
        {/* Barre d'outils supérieure */}
        <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
          <span className="text-white text-sm font-medium truncate pointer-events-auto" title={fichier.nom}>
            {fichier.nom}
          </span>
          <div className="flex items-center gap-2 pointer-events-auto">
            <button
              type="button"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              data-testid="msg-photo-zoom-out"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
              title="Réduire"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span
              className="text-xs text-white/80 w-12 text-center tabular-nums"
              data-testid="msg-photo-scale-value"
              title="Niveau de zoom"
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              data-testid="msg-photo-zoom-in"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center text-white transition-colors"
              title="Agrandir"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={resetZoom}
              disabled={!isZoomed && offset.x === 0 && offset.y === 0}
              data-testid="msg-photo-reset"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 flex items-center justify-center text-white transition-colors"
              title="Réinitialiser le zoom"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <a
              href={dataUrl}
              download={fichier.nom}
              data-testid="msg-photo-download"
              className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 flex items-center justify-center text-white transition-colors"
              title="Télécharger"
            >
              <Download className="w-4 h-4" />
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="msg-photo-close"
              className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              title="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Zone image (draggable quand zoomée) */}
        <div
          className="relative w-full h-[95vh] overflow-hidden flex items-center justify-center select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onDoubleClick={resetZoom}
          style={{ cursor: isZoomed ? (dragRef.current.dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
          data-testid="msg-photo-canvas"
        >
          <img
            ref={imgRef}
            src={dataUrl}
            alt={fichier.nom}
            draggable="false"
            data-testid="msg-photo-img"
            className="max-w-none pointer-events-none"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transformOrigin: 'center center',
              transition: dragRef.current.dragging ? 'none' : 'transform 0.15s ease-out',
              maxWidth: '95vw',
              maxHeight: '95vh',
              width: 'auto',
              height: 'auto',
            }}
          />
        </div>

        {/* Indication clavier bas de dialog (discret) */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/50 text-[10px] pointer-events-none">
          Molette : zoom · Double-clic : réinitialiser · Esc : fermer
        </div>
      </DialogContent>
    </Dialog>
  );
}
